/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-11 13:48:03
 **/

import { produce } from "immer";
import has from "lodash-es/has";
import concat from "lodash-es/concat";
import mergeWith from "lodash-es/mergeWith";
import { nanoid } from "nanoid";
import useProperties, { GLOBAL_SELECTED_ID } from "./useProperties";
import { LocalData, PersistenceDatabase, SessionData } from "@/storage";
import { NormalCrypto } from "@/crypto";

export class Property<T> {
	constructor(
		private readonly key: string,
		private readonly _default_value: T,
		private readonly _options: Partial<{ encrypt: boolean; add_array: boolean; replace: boolean }> | null = null
	) {}

	public get value() {
		return Properties.instance.get<T>(
			this.key,
			this._default_value,
			this._options ? { encrypt: this._options.encrypt ?? false } : undefined
		);
	}

	public set value(value: T) {
		Properties.instance.set(this.key, value, this._options ? this._options : undefined);
	}

	public get is_empty() {
		return (
			this.value === null ||
			this.value === undefined ||
			(typeof this.value === "string" && this.value.length === 0) ||
			(Array.isArray(this.value) && this.value.length === 0) ||
			(typeof this.value === "object" && Object.keys(this.value).length === 0)
		);
	}

	public get is_array() {
		return Array.isArray(this.value);
	}

	public get is_object() {
		return typeof this.value === "object";
	}

	public clear() {
		Properties.instance.clear(this.key);
	}
}

/**
 * The `Properties` class is a singleton that provides an interface for managing
 * and manipulating application properties using state.
 */
export default class Properties {
	static #instance: Properties;
	public static get instance() {
		if (!Properties.#instance) {
			Properties.#instance = new Properties();
		}
		return Properties.#instance;
	}

	static #use_encryption: boolean = true;
	static #use_debug: boolean = false;
	static #use_auto_select: boolean = true;
	static #browser_tab_id: string | undefined = undefined;

	private constructor() {
		Properties.#use_encryption = LocalData.get("useEncryption", false) as boolean;
		Properties.#use_debug = false;
		Properties.#use_auto_select = true;
	}

	public get printlog() {
		return Properties.#use_debug;
	}

	public set use_debug(value: boolean) {
		Properties.#use_debug = value;
	}

	public get use_auto_select() {
		return Properties.#use_auto_select;
	}

	public set use_auto_select(value: boolean) {
		Properties.#use_auto_select = value;
	}

	/**
	 * Retrieves the value of the globally selected ID.
	 *
	 * This getter method provides access to the globally set selection identifier,
	 * which is stored in the `GLOBAL_SELECTED_ID` variable.
	 *
	 * @return {string} The globally selected ID.
	 */
	public get global_selected_id(): string {
		return GLOBAL_SELECTED_ID;
	}

	/**
	 * Retrieves the current encryption setting.
	 *
	 * @return {boolean} Returns true if encryption is enabled; otherwise, false.
	 */
	public get use_encryption(): boolean {
		return Properties.#use_encryption;
	}

	/**
	 * Sets the state of encryption for the application.
	 *
	 * @param {boolean} state - A boolean indicating whether encryption should be enabled (true) or disabled (false).
	 */
	public set use_encryption(state: boolean) {
		Properties.#use_encryption = state;
	}

	/**
	 * Sets the encryption state for the system. Enables encryption if `state` is true and disables it if `state` is false.
	 * Updates the stored data accordingly based on the encryption state.
	 *
	 * @param {boolean} state - A boolean value indicating the desired encryption state.
	 *                          `true` to enable encryption, `false` to disable it.
	 * @return {Promise<boolean>} A promise that resolves to a boolean indicating the current encryption state
	 *                            after the operation. Returns false if the operation fails or no data is found.
	 */
	public async set_encryption(state: boolean): Promise<boolean> {
		const data = await (await PersistenceDatabase.instance.db).get("properties", "properties");
		if (data) {
			if (!state && Properties.#use_encryption) {
				Properties.#use_encryption = false;
				await (
					await PersistenceDatabase.instance.db
				).put("properties", NormalCrypto.decrypt(data), "properties");
			} else if (state && !Properties.#use_encryption) {
				Properties.#use_encryption = true;
				await (
					await PersistenceDatabase.instance.db
				).put("properties", NormalCrypto.encrypt(data), "properties");
			}
			return Properties.#use_encryption;
		}
		return false;
	}

	/**
	 * Getter method for retrieving the currently selected ID.
	 *
	 * @return {string} The ID of the currently selected item.
	 */
	public get selected(): string {
		return useProperties.getState().selected;
	}

	/**
	 * Sets the selected_id value in the state using Immer's produce function to ensure immutability.
	 * Updates the state with the provided id.
	 *
	 * @param {string} id - The identifier to be set as the selected_id in the state.
	 */
	public set selected(id: string | null) {
		useProperties.setState((state) =>
			produce(state, (draft) => {
				if (id !== null) {
					draft.selected = id;
					draft.properties = {
						...draft.properties,
						...(this.use_auto_select
							? {
									[GLOBAL_SELECTED_ID]: {
										...draft.properties[GLOBAL_SELECTED_ID],
										selected: id
									}
								}
							: {}),
						[id]: {
							...draft.properties[id]
						}
					};
				} else {
					draft.selected = GLOBAL_SELECTED_ID;
					delete draft.properties[GLOBAL_SELECTED_ID]["selected"];
				}
			})
		);
	}

	/**
	 * Determines whether the global option is currently selected.
	 *
	 * @return {boolean} True if the global option is selected, otherwise false.
	 */
	public get is_global_selected(): boolean {
		return this.selected === GLOBAL_SELECTED_ID;
	}

	/**
	 * Retrieves all properties stored in the state as a nested record.
	 *
	 * @return {Record<string, Record<string, unknown>>} An object where each key corresponds to a property and its value is another record with associated details.
	 */
	public get all_properties(): Record<string, Record<string, unknown>> {
		return useProperties.getState().properties;
	}

	/**
	 * Retrieves the properties of the currently selected item or falls back to the global selected item.
	 *
	 * @return {Record<string, unknown>} The properties of the selected item or global fallback.
	 */
	public get properties(): Record<string, unknown> {
		try {
			return useProperties.getState().properties[this.selected];
		} catch {
			return useProperties.getState().properties[GLOBAL_SELECTED_ID];
		}
	}

	/**
	 * Checks if the specified key exists in the properties of the currently selected entity.
	 *
	 * @param {string} key - The key to check for existence.
	 * @return {boolean} Returns true if the key exists, false otherwise or in case of an error.
	 */
	public has(key: string): boolean {
		try {
			return has(useProperties.getState().properties[this.selected], key);
		} catch {
			return false;
		}
	}

	/**
	 * Retrieves a value associated with a given key from the properties of the current selection.
	 * If the key does not exist, a default value is returned. Optionally, the stored value can be decrypted.
	 *
	 * @param {string} key - The key of the value to retrieve.
	 * @param {T|null} [def_value=null] - The default value to return if the key does not exist.
	 * @param {{ encrypt: boolean }} [options] - Optional settings.
	 * @param {boolean} [options.encrypt=false] - Whether the stored value should be decrypted before being returned.
	 * @return {T} The value associated with the provided key, or the default value if the key does not exist.
	 */
	public get<T = unknown>(key: string, def_value: T | null = null, options?: { encrypt: boolean }): T {
		try {
			if (!this.has(key)) {
				return def_value as T;
			}
			let { encrypt = false } = options ?? {};
			if (this.use_encryption && encrypt) {
				encrypt = false;
			}
			if (!encrypt) {
				return useProperties.getState().properties[this.selected][key] as T;
			} else {
				return NormalCrypto.decrypt(useProperties.getState().properties[this.selected][key]) as T;
			}
		} catch {
			return def_value as T;
		}
	}

	/**
	 * Sets a value for a given key in the properties state. Updates the state by either merging or replacing
	 * the existing key-value pair. Optionally encrypts the value before storing it.
	 *
	 * @param {string} key - The key under which the value will be stored in the properties state.
	 * @param {unknown} value - The value to be stored in the properties state.
	 * @param {{ encrypt: boolean }} [options] - Optional configuration. If `encrypt` is set to true, the value is encrypted before being stored.
	 * @return {void} This method does not return a value.
	 */
	public set(
		key: string,
		value: unknown,
		options?: Partial<{ encrypt: boolean; add_array: boolean; replace: boolean }>
	): void {
		useProperties.setState((state) =>
			produce(state, (draft) => {
				let { encrypt = false } = options ?? {};
				const { add_array = true, replace = false } = options ?? {};
				if (this.use_encryption && encrypt) {
					encrypt = false;
				}
				try {
					if (Array.isArray(value)) {
						if (encrypt) {
							draft.properties[draft.selected][key] = NormalCrypto.encrypt(value);
						} else {
							if (value.length === 0) {
								draft.properties[draft.selected][key] = [];
							} else {
								if (!replace) {
									draft.properties[draft.selected][key] = concat(
										add_array ? draft.properties[draft.selected][key] : [],
										value
									).filter(Boolean);
								} else {
									draft.properties[draft.selected][key] = value;
								}
							}
						}
					} else if (typeof value === "object") {
						if (Object.keys(value as object).length === 0) {
							draft.properties[draft.selected][key] = {};
						} else {
							if (!replace) {
								draft.properties = mergeWith(draft.properties, {
									[draft.selected]: mergeWith(draft.properties[draft.selected], {
										[key]: !encrypt
											? mergeWith(draft.properties[draft.selected][key], value)
											: NormalCrypto.encrypt(value)
									})
								});
							} else {
								draft.properties[draft.selected][key] = !encrypt ? value : NormalCrypto.encrypt(value);
							}
						}
					} else {
						draft.properties[draft.selected][key] = !encrypt ? value : NormalCrypto.encrypt(value);
					}
				} catch {
					draft.properties = {
						...draft.properties,
						[draft.selected]: {
							...draft.properties[draft.selected],
							[key]: !encrypt ? value : NormalCrypto.encrypt(value)
						}
					};
				}
			})
		);
	}

	/**
	 * Removes a property from the state associated with the provided key.
	 *
	 * @param {string} key - The key of the property to be removed.
	 * @return {void} This method does not return a value.
	 */
	public clear(key: string): void {
		useProperties.setState((state) =>
			produce(state, (draft) => {
				try {
					delete draft.properties[draft.selected][key];
				} catch {
					// ignore
				}
			})
		);
	}

	/**
	 * Checks if the specified key exists within the global properties of the application.
	 *
	 * @param {string} key - The key to check for existence in the global properties.
	 * @return {boolean} Returns true if the key exists, otherwise returns false.
	 */
	public has_global(key: string): boolean {
		try {
			return has(useProperties.getState().properties[GLOBAL_SELECTED_ID], key);
		} catch {
			return false;
		}
	}

	/**
	 * Retrieves a global value associated with a specified key. If the key does not exist, returns a default value.
	 *
	 * @param {string} key - The key used to retrieve the global value.
	 * @param {T | null} [def_value=null] - The default value to return if the key does not exist.
	 * @param {{ encrypt: boolean }} [options] - Options to specify whether the value should be decrypted.
	 * @return {T} The global value associated with the given key or the default value if the key is not found.
	 */
	public get_global<T = unknown>(key: string, def_value: T | null = null, options?: { encrypt: boolean }): T {
		try {
			if (!this.has_global(key)) {
				return def_value as T;
			}
			let { encrypt = false } = options ?? {};
			if (this.use_encryption && encrypt) {
				encrypt = false;
			}
			if (!encrypt) {
				return useProperties.getState().properties[GLOBAL_SELECTED_ID][key] as T;
			} else {
				return NormalCrypto.decrypt(useProperties.getState().properties[GLOBAL_SELECTED_ID][key]) as T;
			}
		} catch {
			return def_value as T;
		}
	}

	/**
	 * Sets a global property with the specified key and value to the internal state, with optional encryption.
	 *
	 * @param {string} key - The key associated with the property to be set.
	 * @param {unknown} value - The value to be stored under the specified key.
	 * @param {{ encrypt: boolean }} [options] - Optional settings for encryption; set `encrypt` to true to encrypt the value.
	 * @return {void} This method does not return a value.
	 */
	public set_global(key: string, value: unknown, options?: { encrypt: boolean }): void {
		useProperties.setState((state) =>
			produce(state, (draft) => {
				let { encrypt = false } = options ?? {};
				if (this.use_encryption && encrypt) {
					encrypt = false;
				}
				try {
					if (typeof draft.properties[GLOBAL_SELECTED_ID][key] === "object" && typeof value === "object") {
						draft.properties[GLOBAL_SELECTED_ID][key] = {
							...(draft.properties[GLOBAL_SELECTED_ID][key] as object),
							...(!encrypt ? value : { [key]: NormalCrypto.encrypt(value) })
						};
					} else {
						draft.properties[GLOBAL_SELECTED_ID][key] = !encrypt ? value : NormalCrypto.encrypt(value);
					}
				} catch {
					draft.properties = {
						...draft.properties,
						[GLOBAL_SELECTED_ID]: {
							...draft.properties[GLOBAL_SELECTED_ID],
							[key]: !encrypt ? value : NormalCrypto.encrypt(value)
						}
					};
				}
			})
		);
	}

	/**
	 * Clears a specified key from the global selected properties.
	 *
	 * @param {string} key - The key to be removed from the global selected properties.
	 * @return {void} This method does not return a value.
	 */
	public clear_global(key: string): void {
		useProperties.setState((state) =>
			produce(state, (draft) => {
				try {
					delete draft.properties[GLOBAL_SELECTED_ID][key];
				} catch {
					// ignore
				}
			})
		);
	}

	/**
	 * Removes a property from the properties object in the application state.
	 *
	 * @param {string} key The key of the property to be removed.
	 * @return {void} Does not return a value.
	 */
	public remove_proerties(key: string): void {
		useProperties.setState((state) =>
			produce(state, (draft) => {
				try {
					delete draft.properties[key];
				} catch {
					// ignore
				}
			})
		);
	}

	/**
	 * Updates the browser tab ID for the currently selected global property.
	 * If a browser_old_unique_id exists, it uses that ID as the new browser tab ID and removes it from the properties.
	 * Otherwise, it generates a new unique ID and sets it as the browser tab ID.
	 *
	 * @return {void} This method does not return any value.
	 */
	public update_browser_tab_id(): void {
		Properties.#browser_tab_id = SessionData.get("brui", undefined) as string | undefined;
		SessionData.remove("brui");
		if (!Properties.#browser_tab_id) {
			Properties.#browser_tab_id = nanoid(64);
		}
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		this.printlog && console.debug("#Properties#", "Updating browser_tab tab id", Properties.#browser_tab_id);
	}

	/**
	 * Disposes of the current browser tab ID by updating the `browser_old_unique_id` property
	 * within the global state using the current `#browser_tab_id`.
	 * This method modifies the state to ensure the "browser_old_unique_id" is properly set.
	 *
	 * @return {void} This method does not return a value.
	 */
	public dispose_browser_tab_id(): void {
		if (Properties.#browser_tab_id) {
			SessionData.set("brui", Properties.#browser_tab_id);
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.printlog && console.debug("#Properties#", "Store browser_tab tab id", Properties.#browser_tab_id);
		}
	}

	/**
	 * Retrieves the unique identifier for the current browser tab, if available.
	 *
	 * @return {string | undefined} The browser tab identifier as a string, or undefined if not set.
	 */
	public get browser_tab_id(): string | undefined {
		// if (!this.#browser_tab_id) {
		// 	this.#browser_tab_id = nanoid(64);
		// }
		return Properties.#browser_tab_id;
	}
}
